import { Eye, Link2, MessageSquare, Play, SquareTerminal } from '@orgus/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useControlState } from '../control/live.ts';
import type { Session } from '@orgus/protocol';
import { Button, EmptyState, ErrorState, SkeletonText } from '@orgus/ui';
import type { UiError } from '../../lib/api.ts';
import { toUiError } from '../../lib/api.ts';
import { openTerminal, useTerms } from './api.ts';
import { noTerminalReason, termForSession } from './model.ts';
import { NewTerminalDialog } from './NewTerminalDialog.tsx';
import { TerminalView } from './TerminalView.tsx';

/**
 * The session drawer's Terminal tab: the live terminal of a session Orgus started in a PTY.
 * A watched session never gets one (ADR 0001); the tab says why and offers a new session instead.
 */
export function SessionTerminalTab({ session, onAnswer }: { session: Session; /** Go to the Activity tab, where the message box is. */ onAnswer?: () => void }) {
  const { terms, phase, error, refresh } = useTerms();
  // With session control on its machine, a watched session still takes messages from Activity.
  const controlled = !!useControlState()?.machines[session.machineId]?.installed;
  const navigate = useNavigate();
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<UiError | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const term = termForSession(terms, session.id);

  const startNew = (
    <>
      <Button variant="primary" icon={<Play />} onClick={() => setNewOpen(true)}>
        Start a session in a terminal
      </Button>
      <NewTerminalDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        prefill={{ machineId: session.machineId, checkoutId: session.checkoutId ?? undefined, mode: 'launch' }}
        onOpened={(id) => navigate(`/terminals?show=${encodeURIComponent(id)}`)}
      />
    </>
  );

  // Tall enough to work in, never taller than the drawer below its header and tabs.
  if (term) return <TerminalView termId={term.id} className="w-full" style={{ height: 'min(35rem, calc(100dvh - 16rem))' }} />;

  if (phase === 'loading') {
    return (
      <div className="p-1" role="status" aria-label="Loading the terminal">
        <SkeletonText lines={5} />
      </div>
    );
  }
  if (phase === 'error') return <ErrorState error={error} onRetry={refresh} />;

  // Session control reaches it from Activity, so that is the one action here (the footer's Open
  // terminal already offers a terminal window on its machine).
  if (noTerminalReason(session) === 'watched' && controlled) {
    return (
      <EmptyState
        icon={<SquareTerminal />}
        title="No shared terminal"
        action={
          onAnswer ? (
            <Button icon={<MessageSquare />} onClick={onAnswer}>
              Answer from Activity
            </Button>
          ) : (
            startNew
          )
        }
      />
    );
  }

  if (noTerminalReason(session) === 'watched') {
    return (
      <EmptyState
        icon={<Eye />}
        title="Orgus only watches this session"
        description="Answer it where it runs, or start a new session in a terminal you can take control of."
        action={startNew}
      />
    );
  }

  const attach = async () => {
    setAttaching(true);
    setAttachError(null);
    try {
      await openTerminal({ machineId: session.machineId, mode: 'attach', sessionId: session.id });
    } catch (e) {
      // Inline, next to what caused it: errors are never a toast (content.md).
      setAttachError(toUiError(e, { what: 'Could not find a terminal for this session.', next: 'Start a new session in a terminal, or try again once its machine is online.' }));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {attachError && (
        <ErrorState
          variant="inline"
          error={attachError}
          action={
            <Button variant="ghost" size="sm" onClick={() => setAttachError(null)}>
              Dismiss
            </Button>
          }
        />
      )}
      <EmptyState
        icon={<SquareTerminal />}
        title="No terminal to take control of"
        description="Look for it again, or start a new session in a terminal."
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button icon={<Link2 />} loading={attaching} onClick={() => void attach()}>
              Look for its terminal
            </Button>
            {startNew}
          </div>
        }
      />
    </div>
  );
}
